import { Injectable } from '@nestjs/common';
import { QueryFailedError, Repository } from 'typeorm';
import { InjectRepository } from '@nestjs/typeorm';

import { Loggable } from '@ip6m/server/shared';

import {
  UserAlreadyExistsException,
  UserIncompleteException,
  UserNotFoundException,
} from '../exceptions';
import { UserEntity } from '../models/user.entity';

@Injectable()
export class UserDataService {
  constructor(
    @InjectRepository(UserEntity)
    private userRepo: Repository<UserEntity>
  ) {}

  @Loggable
  static mapRepoError(user) {
    return (error) => {
      if (error instanceof QueryFailedError) {
        if (/.*doesn't have a default value.*/.test(error.message)) {
          throw new UserIncompleteException(error.message);
        }

        if (/.*Duplicate entry.*for key.*/.test(error.message)) {
          throw new UserAlreadyExistsException(user.email);
        }
      }

      throw error;
    };
  }

  /**
   * @throws UserAlreadyExistsException
   * @throws UserIncompleteException
   */
  @Loggable
  async create(user: Omit<UserEntity, 'id' | 'isActive'>): Promise<UserEntity> {
    const existingUser = await this.userRepo
      .findOne({
        email: user.email,
      })
      .catch(UserDataService.mapRepoError(user));
    if (existingUser) {
      throw new UserAlreadyExistsException(user.email);
    }

    return this.userRepo
      .save({ ...user })
      .catch(UserDataService.mapRepoError(user));
  }

  /**
   * throws UserNotFoundException
   */
  @Loggable
  get(id: number): Promise<UserEntity> {
    return this.userRepo.findOneOrFail({ where: { id }, relations: ["roles"] }).catch(() => {
      throw new UserNotFoundException(id.toString());
    });
  }

  /**
   * throws UserNotFoundException
   */
  @Loggable
  async update(
    id: number,
    partialUser: Partial<UserEntity>
  ): Promise<UserEntity> {
    const user = await this.get(id);
    for (const property of Object.keys(partialUser)) {
      user[property] = partialUser[property];
    }

    return this.userRepo
      .save(user)
      .catch(UserDataService.mapRepoError(partialUser));
  }

  /**
   * throws UserNotFoundException
   */
  @Loggable
  async delete(id: number): Promise<void> {
    await this.get(id);
    await this.userRepo.delete({ id });
  }

  @Loggable
  findByEmail(email: string): Promise<UserEntity[]> {
    return this.userRepo.find({ where: { email }, relations: ["roles"] });
  }
}
